import { createPortal } from "react-dom"
import { ChevronDown, SlidersHorizontal } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import { controlBase, onFocus, onBlur } from "@/app/lib/tableStyles"
import { usePopover } from "@/app/hooks/usePopover"

interface MoreFiltersProps {
  /** How many of the tucked-away filters are active — shown as a badge. */
  activeCount: number
  theme: ClientTheme
  children: React.ReactNode
}

/** Caps-label section heading for rows inside the panel. */
export function MoreFiltersSection({
  label,
  theme,
  children,
}: {
  label: string
  theme: ClientTheme
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col" style={{ gap: "8px" }}>
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "10px",
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: theme.textMuted,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  )
}

/**
 * Overflow home for the less-used filters: a controlBase trigger with an
 * active-count badge, opening a portaled panel. Children are the actual
 * filter controls (pill groups, segmented toggles) — no nested dropdowns,
 * so outside-click handling stays simple.
 */
export function MoreFilters({ activeCount, theme, children }: MoreFiltersProps) {
  const { open, setOpen, triggerRef, popoverRef, popoverStyle } = usePopover<HTMLDivElement>()

  return (
    <div ref={triggerRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={onFocus(theme)}
        onBlur={onBlur(theme)}
        className="flex items-center gap-1.5"
        style={{
          ...controlBase(theme),
          paddingLeft: "10px",
          paddingRight: "10px",
          cursor: "pointer",
          color: activeCount > 0 ? theme.textPrimary : theme.textMuted,
        }}
      >
        <SlidersHorizontal size={13} />
        More Filters
        {activeCount > 0 && (
          <span
            className="flex items-center justify-center rounded-full flex-shrink-0"
            style={{
              minWidth: "16px",
              height: "16px",
              padding: "0 4px",
              fontFamily: "var(--font-ui)",
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: theme.brandPrimary,
              color: theme.textOnBrand,
            }}
          >
            {activeCount}
          </span>
        )}
        <ChevronDown
          size={12}
          style={{
            color: theme.textDim,
            transform: open ? "rotate(180deg)" : undefined,
            transition: "transform 140ms ease",
          }}
        />
      </button>

      {/* Portaled to <body> so the card's overflow-hidden can't clip it. */}
      {open &&
        popoverStyle &&
        createPortal(
          <div
            ref={popoverRef}
            className="rounded-lg flex flex-col"
            style={{
              ...popoverStyle,
              // Taller than the list-dropdown cap — this panel holds several
              // control groups; it scrolls only when the viewport is short.
              maxHeight: Math.max(220, Math.min(460, window.innerHeight - popoverStyle.top - 12)),
              width: "320px",
              padding: "14px",
              gap: "14px",
              overflowY: "auto",
              zIndex: 60,
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  )
}
